import { ORDER_STATUSES, ORDER_STATUS_OPTIONS, formatFulfillmentType, formatOrderStatus } from "@/utils/format.js";

/** Four visual pipeline steps for customers (CONFIRMED + PREPARING share step 2). */
export const ORDER_PROGRESS_STEPS = [
    { id: "received", label: "Reçue" },
    { id: "preparing", label: "Préparation" },
    { id: "ready", label: "Prête" },
    { id: "done", label: "Terminée" }
];

/** Full admin pipeline — one node per non-cancelled status. */
export const ADMIN_ORDER_PROGRESS_STEPS = ORDER_STATUS_OPTIONS
    .filter((option) => option.value !== ORDER_STATUSES.CANCELLED)
    .map((option) => ({
        id: option.value.toLowerCase(),
        label: option.label,
        status: option.value
    }));

/**
 * Index of the current customer step (0–3), or -1 when cancelled / unknown terminal.
 */
export function getOrderProgressStepIndex(status) {
    switch (status) {
        case ORDER_STATUSES.PENDING:
            return 0;
        case ORDER_STATUSES.CONFIRMED:
        case ORDER_STATUSES.PREPARING:
            return 1;
        case ORDER_STATUSES.READY:
            return 2;
        case ORDER_STATUSES.COMPLETED:
            return 3;
        case ORDER_STATUSES.CANCELLED:
            return -1;
        default:
            return 0;
    }
}

/**
 * Index of the current admin step (0–4), or -1 when cancelled.
 */
export function getAdminOrderProgressStepIndex(status) {
    if (status === ORDER_STATUSES.CANCELLED) {
        return -1;
    }
    const index = ADMIN_ORDER_PROGRESS_STEPS.findIndex((step) => step.status === status);
    return index >= 0 ? index : 0;
}

export function isActiveCustomerOrder(status) {
    return status
        && status !== ORDER_STATUSES.COMPLETED
        && status !== ORDER_STATUSES.CANCELLED;
}

export function getOrderProgressHeadline(status, fulfillmentType) {
    if (status === ORDER_STATUSES.CANCELLED) {
        return "Commande annulée";
    }
    if (status === ORDER_STATUSES.COMPLETED) {
        return "Commande terminée";
    }
    if (status === ORDER_STATUSES.READY) {
        return fulfillmentType === "DELIVERY"
            ? "Prête pour la livraison"
            : "Prête à retirer";
    }
    if (status === ORDER_STATUSES.PREPARING) {
        return "En préparation";
    }
    if (status === ORDER_STATUSES.CONFIRMED) {
        return "Commande confirmée";
    }
    if (status === ORDER_STATUSES.PENDING) {
        return "Commande reçue";
    }
    return formatOrderStatus(status) || "Commande";
}

export function getOrderProgressCaption(status, fulfillmentType, { mode = "customer" } = {}) {
    if (mode === "admin") {
        if (status === ORDER_STATUSES.CANCELLED) {
            return "Cette commande a été annulée.";
        }
        if (status === ORDER_STATUSES.COMPLETED) {
            return "Commande terminée — stock alloué et reçu éventuel émis.";
        }
        if (status === ORDER_STATUSES.READY) {
            return fulfillmentType === "DELIVERY"
                ? "Prête — en attente de livraison."
                : "Prête — en attente de retrait en magasin.";
        }
        if (status === ORDER_STATUSES.PREPARING) {
            return "Préparation en cours.";
        }
        if (status === ORDER_STATUSES.CONFIRMED) {
            return "Confirmée — à préparer.";
        }
        if (status === ORDER_STATUSES.PENDING) {
            return "Nouvelle commande — en attente de confirmation.";
        }
        return "";
    }

    if (status === ORDER_STATUSES.CANCELLED) {
        return "Cette commande ne sera pas traitée.";
    }
    if (status === ORDER_STATUSES.COMPLETED) {
        return fulfillmentType === "DELIVERY"
            ? "Livraison effectuée — merci pour votre achat."
            : "Retrait effectué — merci pour votre achat.";
    }
    if (status === ORDER_STATUSES.READY) {
        return fulfillmentType === "DELIVERY"
            ? "Votre commande est prête — en attente de livraison."
            : "Votre commande est prête — venez la retirer en magasin.";
    }
    if (status === ORDER_STATUSES.PREPARING) {
        return "Nous préparons vos articles.";
    }
    if (status === ORDER_STATUSES.CONFIRMED) {
        return "Votre commande a été confirmée par le magasin.";
    }
    if (status === ORDER_STATUSES.PENDING) {
        return "En attente de confirmation par le magasin.";
    }
    return "";
}

export function getOrderProgressMeta(order) {
    if (!order) {
        return "";
    }
    return formatFulfillmentType(order.fulfillmentType) || "";
}

/**
 * @param {object} order
 * @param {{ mode?: "customer"|"admin" }} [options]
 */
export function buildOrderProgressModel(order, { mode = "customer" } = {}) {
    const status = order?.status;
    const fulfillmentType = order?.fulfillmentType;
    const cancelled = status === ORDER_STATUSES.CANCELLED;
    const completed = status === ORDER_STATUSES.COMPLETED;
    const stepsDef = mode === "admin" ? ADMIN_ORDER_PROGRESS_STEPS : ORDER_PROGRESS_STEPS;
    const currentIndex = mode === "admin"
        ? getAdminOrderProgressStepIndex(status)
        : getOrderProgressStepIndex(status);

    const steps = stepsDef.map((step, index) => {
        let state = "upcoming";
        if (cancelled) {
            state = "cancelled";
        } else if (completed || index < currentIndex) {
            state = "done";
        } else if (index === currentIndex) {
            state = "current";
        }
        return { ...step, index, state };
    });

    return {
        status,
        fulfillmentType,
        currentIndex,
        cancelled,
        completed,
        mode,
        headline: getOrderProgressHeadline(status, fulfillmentType),
        caption: getOrderProgressCaption(status, fulfillmentType, { mode }),
        meta: getOrderProgressMeta(order),
        steps
    };
}
